import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Badge, Text } from '@chakra-ui/react';

import Navbar from '../../GlobalComponents/Navbar/Navbar';
import { EmergencyTicketCard } from '../Subscription/Components/EmergencyTicketCard';
import { getTicketsBySubscriberId } from '../../http/index';
import { EmergencyTicketSummary } from '../Subscription/Types/types';
import styles from './Tickets.module.css';

// ---------------------------------------------------------------------------
// State interface
// ---------------------------------------------------------------------------
interface TicketsState {
  ticketsLoading: boolean;
  ticketsLoaded: boolean;
  tickets: EmergencyTicketSummary[];
  ticketsError: boolean;
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------
export const TicketsPage = () => {
  // Redux reads
  const subscriberId = useSelector((state: any) => state.subscriber.subscriberId);

  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------
  const [ticketsState, setTicketsState] = useState<TicketsState>({
    ticketsLoading: false,
    ticketsLoaded: false,
    tickets: [],
    ticketsError: false,
  });

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------
  useEffect(() => {
    const fetchTickets = async () => {
      setTicketsState({
        ticketsLoading: true,
        ticketsLoaded: false,
        tickets: [],
        ticketsError: false,
      });
      try {
        const res = await getTicketsBySubscriberId(subscriberId);
        const raw: EmergencyTicketSummary[] = res?.data?.tickets ?? [];
        const sorted = raw.slice().sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        setTicketsState({
          ticketsLoading: false,
          ticketsLoaded: true,
          tickets: sorted,
          ticketsError: false,
        });
      } catch {
        setTicketsState({
          ticketsLoading: false,
          ticketsLoaded: false,
          tickets: [],
          ticketsError: true,
        });
      }
    };

    if (subscriberId) {
      fetchTickets();
    }
  }, [subscriberId]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <>
      <Navbar />
      <div className={styles.container}>
        {/* Page header */}
        <div className={styles['page-header']}>
          <h2 className={styles['page-title']}>Emergency Tickets</h2>
          {ticketsState.ticketsLoaded && !ticketsState.ticketsError && (
            <Badge colorScheme="teal" fontSize="14px" px={3} py={1} borderRadius="4px">
              {ticketsState.tickets.length}
            </Badge>
          )}
        </div>

        {/* Content area */}
        <div className={styles['content-area']}>
          {ticketsState.ticketsLoading && (
            <Text className={styles['state-message']}>
              Loading your emergency tickets…
            </Text>
          )}

          {ticketsState.ticketsError && (
            <Text className={styles['error-message']}>
              Failed to load emergency tickets. Please try again.
            </Text>
          )}

          {ticketsState.ticketsLoaded && !ticketsState.ticketsError && ticketsState.tickets.length === 0 && (
            <Text className={styles['state-message']}>
              No emergency tickets found.
            </Text>
          )}

          {ticketsState.ticketsLoaded && !ticketsState.ticketsError && ticketsState.tickets.length > 0 && (
            <div className={styles['tickets-list']}>
              {ticketsState.tickets.map(ticket => (
                <EmergencyTicketCard key={ticket._id} ticket={ticket} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
